import { createContext, useContext, useMemo } from 'react';
import type { FC, ReactNode } from 'react';

import LoadingScreen from '@app/components/agreement-loader/loading-screen';
import { useDocumentTranslationsLoader } from './use-document-translations-loader';

type DocumentTranslationsContextProps = Omit<
  ReturnType<typeof useDocumentTranslationsLoader>,
  'isReady'
>;

export const DocumentTranslationsContext = createContext<DocumentTranslationsContextProps | null>(
  null,
);

type Props = {
  children: ReactNode;
  isGuest: boolean;
  language: string;
};

export const DocumentTranslationsLoader: FC<Props> = ({ children, isGuest, language }: Props) => {
  const { isReady, t, tx } = useDocumentTranslationsLoader({
    isGuest,
    language,
  });

  const contextValue: DocumentTranslationsContextProps = useMemo(
    () => ({
      t,
      tx,
    }),
    [
      t,
      tx,
    ],
  );

  if (!isReady) {
    return <LoadingScreen />;
  }

  return (
    <DocumentTranslationsContext.Provider value={contextValue}>
      {children}
    </DocumentTranslationsContext.Provider>
  );
};

export const useDocumentTranslations = (): DocumentTranslationsContextProps => {
  const context = useContext(DocumentTranslationsContext);

  if (!context) {
    throw new Error('useDocumentTranslations must be used within DocumentTranslationsContext');
  }

  return context;
};
